import { Head } from "@inertiajs/react";

import { FinalCta } from "@/components/final-cta";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";

export default function Privacy() {
  return (
    <>
      <Head title="Privacy Policy" />

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <PublicHeader />

        {/* Privacy Policy Content */}
        <div className="container mx-auto max-w-4xl px-4 py-12">
          <div className="rounded-lg bg-white p-8 shadow-lg dark:bg-gray-800">
            <h1 className="mb-8 text-3xl font-bold text-gray-900 dark:text-white">Privacy Policy</h1>

            <div className="prose max-w-none space-y-6 text-gray-600 dark:text-gray-300">
              <p>
                OG Pilot respects your privacy and is committed to protecting the personal data you share with us when
                you explore our marketing site, create an account, generate one-off previews, or connect a domain for
                autopilot and API access, including through supported integrations such as the official WordPress plugin
                and the OG Pilot MCP connector used from Claude custom connectors or ChatGPT developer mode. This
                Privacy Policy explains what we collect, how we use it, and the choices you have.
              </p>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Information We Collect</h2>
                <p>
                  We collect the information you provide directly (such as your name, email address, billing details,
                  domain names you connect, DNS verification records, API key metadata, and any copy or brand assets you
                  upload for image generation) as well as automatically collected technical data (including log files,
                  device details, cookies, and analytics events). We also store the URLs you submit for one-off or
                  automated generation, along with the resulting assets. If you contact us for support, we retain the
                  details you share so we can resolve your request.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">WordPress Plugin Data</h2>
                <p>
                  When you use the official WordPress plugin, we process integration-specific data needed to operate the
                  connection and generate images, including OAuth installation identifiers, token metadata, connected
                  site hostname/URL, selected template settings, and generation payload fields you configure (for
                  example title, description, logo URL, custom image URL, and color settings). We also process
                  subscription state to determine whether new generation requests are permitted.
                </p>
                <p>
                  We do not use plugin data for broad, unrelated tracking. Requests sent by the plugin are limited to
                  authentication, subscription checks, and OG image generation workflows.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">MCP Connector Data</h2>
                <p>
                  When you connect OG Pilot as a custom Claude connector or a ChatGPT developer-mode MCP server, we
                  process OAuth client registration details, authorization codes, access and refresh token metadata, the
                  page and image URLs you submit, optional style and compression settings, and the resulting image
                  assets. Requests from the connector are limited to authentication, generate_og_image,
                  debug_open_graph, compress_image, and extract_color_palette.
                </p>
                <p>
                  Connecting does not require an OG Pilot account. Guest connector sessions are identified by the OAuth
                  grant, not a passworded login. You can disconnect the connector in Claude or ChatGPT, or contact
                  support to revoke access. If you choose the optional signed-in account path, generations can use that
                  account&apos;s credits. Token metadata is retained for account security, abuse prevention, and
                  auditability for as long as reasonably necessary.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">How We Use Data</h2>
                <p>
                  We process personal data to operate and improve OG Pilot, fulfill and deliver your generated assets,
                  verify domain ownership, issue API keys, provide customer support, personalize your experience, send
                  transactional communications, and maintain the security of our services. For integrations like the
                  WordPress plugin, we also use this data to maintain OAuth sessions, validate authorized site
                  connections, and enforce plan/trial access controls for generation requests. We may also use
                  aggregated, de-identified information to understand usage trends and improve features.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">AI Generation and Third Parties</h2>
                <p>
                  To create images, we may transmit the prompts, URLs, and other materials you supply to trusted
                  infrastructure partners and AI model providers. We may also fetch metadata (such as titles, images,
                  and colors) from the URLs you submit to build previews. These processors handle the content solely to
                  deliver the requested outputs and are bound by contractual confidentiality and security obligations.
                  Do not upload or request content that includes personal data you are not authorized to share.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Legal Bases</h2>
                <p>
                  We rely on legitimate interests to operate and secure the service, performance of a contract to
                  deliver requested features, and your consent where required by law (for example, when storing optional
                  cookies or sending marketing communications).
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Data Retention and Security</h2>
                <p>
                  We retain personal data only for as long as necessary to provide OG Pilot, meet legal obligations, and
                  resolve disputes. Generated assets, prompts, API activity logs, and related content may be kept so you
                  can access past work, but you can request deletion at any time. OAuth and integration metadata are
                  retained for account security, fraud prevention, abuse mitigation, and auditability for as long as
                  reasonably necessary. We employ commercially reasonable safeguards to protect against unauthorized
                  access, alteration, disclosure, or destruction of personal data.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Sharing and Disclosure</h2>
                <p>
                  We do not sell your personal information. We share data with service providers who help us operate OG
                  Pilot (such as cloud hosting, CDN delivery, payment processors, analytics, and AI platforms) and only
                  to the extent necessary for them to perform their work. We may disclose information if required to
                  comply with law, protect our rights, or respond to lawful requests from public authorities.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Your Choices</h2>
                <p>
                  You can update account details, revoke API keys, disconnect domains, opt out of non-essential
                  communications, request deletion of stored prompts and assets, or exercise data protection rights by
                  contacting us. For WordPress installations, you can disconnect the plugin to revoke future OAuth-based
                  API access. For the MCP connector, disconnect the custom connector in Claude or ChatGPT to stop new
                  requests. Depending on where you live, you may also have rights to access, correct, or port your
                  personal data and to object to certain processing.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Children&apos;s Privacy</h2>
                <p>
                  OG Pilot is not directed to children under 16, and we do not knowingly collect personal data from
                  children. If you believe a child has provided us with personal information, please contact us so we
                  can delete it.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Updates to This Policy</h2>
                <p>
                  We may update this Privacy Policy to reflect changes to our practices or for legal reasons. We will
                  post the revised policy with an updated effective date and, where required, notify you through the
                  service or by email.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Contact Us</h2>
                <p>
                  If you have questions about this policy or our data practices, email us at support@ogpilot.com. We
                  will respond within a reasonable timeframe.
                </p>
              </div>

              <p className="text-sm text-gray-500 dark:text-gray-400">
                Effective as of{" "}
                {new Date().toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
                .
              </p>
            </div>
          </div>
        </div>

        <FinalCta
          title="Build privacy-first previews for one-off or autopilot"
          description="OG Pilot helps teams ship branded OG images while keeping customer data secure. Generate one-off assets, connect domains for automation, and share analytics without slowing down launches."
          highlights={[
            { icon: "🔐", text: "Privacy-aligned workflows" },
            { icon: "📊", text: "Client-ready reporting" },
          ]}
          submitLabel="Launch OG Pilot"
        />
      </div>
      <SiteFooter />
    </>
  );
}
